import {
  AspectRatio,
  Drawer,
  FocusTrap,
  Image,
  Modal,
  Skeleton,
  Tabs,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { ReactionsType } from './api'
import ReactionsPopover from './ReactionsPopover'
import { ReactComponent as ThumbsUpIcon } from '../assets/thumbs_up.svg'
import { ReactComponent as PartyingFaceIcon } from '../assets/partying_face.svg'
import { ReactComponent as RocketIcon } from '../assets/rocket.svg'
import classes from './Socials.module.css'
import { Typography } from '../common/typography'
import { useCompetitionId } from '../common/hooks'
import { useArenaPost } from '../arena/api'
import { useState } from 'react'

function ReactionRow({
  reaction_type,
  user_full_name,
  user_photo,
}: {
  reaction_type: string
  user_full_name: string
  user_photo: string
}) {
  const [playerImageLoaded, setPlayerImageLoaded] = useState<boolean>(false)
  const isPlaceholderPhoto = /\.svg$/i.test(user_photo)

  return (
    <div className={classes.reactionRow}>
      <div className={classes.reactionRowInner}>
        <AspectRatio ratio={1 / 1} className={classes.aspectRatio}>
          {!playerImageLoaded && <Skeleton circle height="100%" />}

          <Image
            src={user_photo}
            alt={user_full_name}
            className={classes.playerImage}
            onLoad={() => setPlayerImageLoaded(true)}
            data-loaded={playerImageLoaded}
            fit={isPlaceholderPhoto ? 'contain' : 'scale-down'}
          />
        </AspectRatio>
        <Typography lh="140">{user_full_name}</Typography>
      </div>
      {reaction_type === 'thumbs_up' && <ThumbsUpIcon />}
      {reaction_type === 'rocket' && <RocketIcon />}
      {reaction_type === 'partying_face' && <PartyingFaceIcon />}
    </div>
  )
}

export default function Reactions({
  post_id,
  reactions,
  isRoar,
}: {
  post_id: number
  reactions: ReactionsType
  isRoar: boolean
}) {
  const [opened, { open, close }] = useDisclosure(false)
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const competition_id = useCompetitionId()
  const { data: arenaPost } = useArenaPost({
    competition_id,
    post_id: post_id,
    should_load: opened,
  })

  return (
    <div className={classes.reactionsContainer}>
      <div className={classes.reactionCounter}>
        <ReactionsPopover post_id={post_id} isRoar={isRoar} />
        <Typography
          fs="xs"
          fc={{
            light: isRoar ? 'white' : 'gray-9',
            dark: isRoar ? 'white' : 'gray-3',
          }}
          lh="150"
          className={classes.counter}
        >
          {(reactions.rocket ?? 0) +
            (reactions.red_heart ?? 0) +
            (reactions.partying_face ?? 0) +
            (reactions.thumbs_up ?? 0)}
        </Typography>
      </div>

      <div className={classes.reactionsEmojis} onClick={open}>
        {reactions.rocket && reactions.rocket > 0 && (
          <RocketIcon width={16} height={16} />
        )}
        {reactions.partying_face && reactions.partying_face > 0 && (
          <PartyingFaceIcon width={16} height={16} />
        )}
        {reactions.thumbs_up && reactions.thumbs_up > 0 && (
          <ThumbsUpIcon width={16} height={16} />
        )}
      </div>

      {isDesktop ? (
        <Modal.Root
          opened={opened}
          onClose={close}
          centered
          size="lg"
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{ header: classes.header, body: classes.body }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <FocusTrap.InitialFocus />
            <Modal.Header>
              <Modal.Title>
                <Typography fw={500} lh="125">
                  Reactions
                </Typography>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <Tabs
                defaultValue="all"
                classNames={{
                  root: classes.tabsRoot,
                  list: classes.list,
                  tab: classes.tab,
                  tabLabel: classes.tabLabel,
                }}
              >
                <Tabs.List>
                  <Tabs.Tab value="all">All</Tabs.Tab>
                  <Tabs.Tab value="thumbs_up">
                    <ThumbsUpIcon width={16} height={16} />
                  </Tabs.Tab>
                  <Tabs.Tab value="partying_face">
                    <PartyingFaceIcon width={16} height={16} />
                  </Tabs.Tab>
                  <Tabs.Tab value="rocket">
                    <RocketIcon width={16} height={16} />
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="all">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions.map((reaction) => (
                      <ReactionRow
                        key={reaction.reaction_id}
                        reaction_type={reaction.reaction_type}
                        user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                        user_photo={reaction.user_photo}
                      />
                    ))}
                </Tabs.Panel>
                <Tabs.Panel value="thumbs_up">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions
                      .filter(
                        (reaction) => reaction.reaction_type === 'thumbs_up'
                      )
                      .map((reaction) => (
                        <ReactionRow
                          key={reaction.reaction_id}
                          reaction_type={reaction.reaction_type}
                          user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                          user_photo={reaction.user_photo}
                        />
                      ))}
                </Tabs.Panel>
                <Tabs.Panel value="partying_face">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions
                      .filter(
                        (reaction) => reaction.reaction_type === 'partying_face'
                      )
                      .map((reaction) => (
                        <ReactionRow
                          key={reaction.reaction_id}
                          reaction_type={reaction.reaction_type}
                          user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                          user_photo={reaction.user_photo}
                        />
                      ))}
                </Tabs.Panel>
                <Tabs.Panel value="rocket">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions
                      .filter((reaction) => reaction.reaction_type === 'rocket')
                      .map((reaction) => (
                        <ReactionRow
                          key={reaction.reaction_id}
                          reaction_type={reaction.reaction_type}
                          user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                          user_photo={reaction.user_photo}
                        />
                      ))}
                </Tabs.Panel>
              </Tabs>
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={opened}
          onClose={close}
          position="bottom"
          classNames={{
            header: classes.header,
            body: classes.body,
            content: classes.content,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <FocusTrap.InitialFocus />
            <Drawer.Header>
              <Drawer.Title>
                <Typography fs="lg" fw={500} lh="125">
                  Reactions
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
              <Tabs
                defaultValue="all"
                classNames={{
                  root: classes.tabsRoot,
                  list: classes.list,
                  tab: classes.tab,
                  tabLabel: classes.tabLabel,
                }}
              >
                <Tabs.List>
                  <Tabs.Tab value="all">All</Tabs.Tab>
                  <Tabs.Tab value="thumbs_up">
                    <ThumbsUpIcon width={16} height={16} />
                  </Tabs.Tab>
                  <Tabs.Tab value="partying_face">
                    <PartyingFaceIcon width={16} height={16} />
                  </Tabs.Tab>
                  <Tabs.Tab value="rocket">
                    <RocketIcon width={16} height={16} />
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="all">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions.map((reaction) => (
                      <ReactionRow
                        key={reaction.reaction_id}
                        reaction_type={reaction.reaction_type}
                        user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                        user_photo={reaction.user_photo}
                      />
                    ))}
                </Tabs.Panel>
                <Tabs.Panel value="thumbs_up">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions
                      .filter(
                        (reaction) => reaction.reaction_type === 'thumbs_up'
                      )
                      .map((reaction) => (
                        <ReactionRow
                          key={reaction.reaction_id}
                          reaction_type={reaction.reaction_type}
                          user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                          user_photo={reaction.user_photo}
                        />
                      ))}
                </Tabs.Panel>
                <Tabs.Panel value="partying_face">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions
                      .filter(
                        (reaction) => reaction.reaction_type === 'partying_face'
                      )
                      .map((reaction) => (
                        <ReactionRow
                          key={reaction.reaction_id}
                          reaction_type={reaction.reaction_type}
                          user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                          user_photo={reaction.user_photo}
                        />
                      ))}
                </Tabs.Panel>
                <Tabs.Panel value="rocket">
                  {arenaPost?.reactions &&
                    arenaPost.reactions.length > 0 &&
                    arenaPost.reactions
                      .filter((reaction) => reaction.reaction_type === 'rocket')
                      .map((reaction) => (
                        <ReactionRow
                          key={reaction.reaction_id}
                          reaction_type={reaction.reaction_type}
                          user_full_name={`${reaction.user_first_name} ${reaction.user_last_name}`}
                          user_photo={reaction.user_photo}
                        />
                      ))}
                </Tabs.Panel>
              </Tabs>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  )
}
